import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../services/api';
import { products as hardcodedProducts } from '../data/products';

const Products = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const itemsPerPage = 4;
  const totalPages = Math.ceil(products.length / itemsPerPage);

  useEffect(() => {
    // Use hardcoded products
    setProducts(hardcodedProducts);
  }, []);

  useEffect(() => {
    updateDisplayedProducts();
  }, [products, currentPage]);

  const updateDisplayedProducts = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedProducts(products.slice(startIndex, endIndex));
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const openDemo = (demoUrl?: string) => {
    if (demoUrl) {
      window.open(demoUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-gradient-to-br from-card1 to-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-center items-center py-32">
            <div className="text-center">
              <div className="w-20 h-20 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-8"></div>
              <h2 className="text-2xl font-bold text-primary mb-4">Loading Products</h2>
              <p className="text-lg text-primaryLight">Discovering our innovative solutions...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="min-h-screen bg-gradient-to-br from-card1 to-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-center items-center py-32">
            <div className="text-center">
              <div className="w-24 h-24 bg-red100 rounded-full flex items-center justify-center mx-auto mb-8">
                <i className="fas fa-exclamation-triangle text-4xl text-red500"></i>
              </div>
              <h2 className="text-3xl font-bold text-primary mb-4">Oops! Something went wrong</h2>
              <p className="text-xl text-red500 mb-8">{error}</p>
              <button 
                onClick={() => setError(null)} // Clear error on button click
                className="bg-gradient-to-r from-secondary to-secondaryDark text-primary font-bold px-8 py-4 rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 transform hover:scale-105"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-gradient-to-br from-card1 via-blue100/30 to-white py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-10 w-40 h-40 bg-blue500/5 rounded-full animate-float"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-green600/5 rounded-full animate-float" style={{ animationDelay: '3s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-secondary/5 rounded-full animate-float" style={{ animationDelay: '1.5s' }}></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20 animate-fade-in">
          <div className="w-20 h-20 bg-gradient-to-br from-secondary to-secondaryDark rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-glow">
            <i className="fas fa-cube text-3xl text-white"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-primary mb-8">
            Our Products
          </h1>
          <p className="text-xl md:text-2xl text-primaryLight max-w-4xl mx-auto leading-relaxed">
            Driving Innovation in Healthcare Staffing. At VitalHive, we merge technology innovation and artificial intelligence to power enterprise solutions in healthcare. We simplify complex workflows for healthcare facilities and professionals alike.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {displayedProducts.map((product, index) => (
            <div 
              key={product.id}
              className="bg-card-gradient rounded-3xl shadow-soft hover:shadow-strong transition-all duration-500 transform hover:-translate-y-4 overflow-hidden group"
              style={{ animationDelay: `${index * 200}ms` }}
            >
              {/* Product Image */}
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={product.imageUrl} 
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/20 to-transparent"></div>
                {product.isNew && (
                  <div className="absolute top-6 left-6">
                    <span className="bg-badgePrimary text-white px-4 py-2 rounded-full text-sm font-bold shadow-soft">
                      New
                    </span>
                  </div>
                )}
                <div className="absolute bottom-6 left-6 right-6">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{product.name}</h2>
                  <p className="text-white/90 text-lg">{product.featureShort}</p>
                </div>
              </div>

              {/* Product Content */}
              <div className="p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue500 to-blue700 rounded-xl flex items-center justify-center">
                    <i className="fas fa-tag text-white text-lg"></i>
                  </div>
                  <span className="bg-blue100 text-blue700 px-4 py-2 rounded-full text-sm font-semibold">
                    {product.category}
                  </span>
                </div>

                <p className="text-lg text-primaryLight mb-8 leading-relaxed">{product.description}</p>

                {/* Features Preview */}
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-primary mb-4">Key Features:</h4>
                  <ul className="space-y-3">
                    {product.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-green100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="fas fa-check text-green600 text-xs"></i>
                        </div>
                        <span className="text-primaryLight">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link 
                    to={`/products/${product.id}`}
                    className="flex-1 bg-gradient-to-r from-primary to-primaryDark text-white font-bold py-4 px-6 rounded-2xl text-center hover:shadow-strong transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-3"
                  >
                    <i className="fas fa-info-circle"></i>
                    <span>More Details</span>
                  </Link>
                  {product.demoUrl && (
                    <button 
                      onClick={() => openDemo(product.demoUrl)}
                      className="flex-1 bg-gradient-to-r from-secondary to-secondaryDark text-primary font-bold py-4 px-6 rounded-2xl hover:shadow-strong transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-3"
                    >
                      <i className="fas fa-play-circle"></i>
                      <span>Try Demo</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-6">
            <button 
              onClick={previousPage}
              disabled={currentPage === 1}
              className="px-6 py-3 bg-gradient-to-r from-primary to-primaryDark text-white font-bold rounded-2xl hover:shadow-strong transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-soft flex items-center space-x-2"
            >
              <i className="fas fa-chevron-left"></i>
              <span>Previous</span>
            </button>
            
            <div className="flex items-center space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-12 h-12 rounded-xl font-bold transition-all duration-300 ${
                    currentPage === page
                      ? 'bg-gradient-to-r from-secondary to-secondaryDark text-primary shadow-strong'
                      : 'bg-white text-primary border-2 border-iconFourth hover:border-blue500 hover:shadow-soft'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button 
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="px-6 py-3 bg-gradient-to-r from-primary to-primaryDark text-white font-bold rounded-2xl hover:shadow-strong transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-soft flex items-center space-x-2"
            >
              <span>Next</span>
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-blue100 via-card1 to-green100 rounded-3xl p-12 shadow-soft">
            <h3 className="text-3xl md:text-4xl font-bold text-primary mb-6">
              Ready to Transform Your Healthcare Staffing?
            </h3>
            <p className="text-xl text-primaryLight mb-8 max-w-3xl mx-auto">
              Join thousands of healthcare professionals and facilities already using VitalHive to streamline their operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://app.vitalhivetech.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-secondary to-secondaryDark text-primary font-bold px-8 py-4 rounded-2xl shadow-soft hover:shadow-strong transition-all duration-300 transform hover:scale-105"
              >
                Get Started Today
              </a>
              <Link 
                to="/contact"
                className="bg-transparent border-2 border-primary text-primary font-bold px-8 py-4 rounded-2xl hover:bg-primary hover:text-white transition-all duration-300"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products; 